import {
  ApplicantStatus,
  ExecutionModes,
  VerificationStatus,
  VerificationNames,
  ApplicantSettings
} from "shared/types";
import { Applicant, Application } from "types/application";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import { DefinedQueryObserverResult } from "@tanstack/react-query";
import CreditReportSlice from "..";
import { ManualReportView } from "views/applications-id-report/components/ReportsFallback/ManualReportView";
import { SkippedReportView } from "views/applications-id-report/components/ReportsFallback/SkippedReportView";
import { ProgressReportView } from "views/applications-id-report/components/ReportsFallback/ProgressReportView";
import { PendingReportView } from "views/applications-id-report/components/ReportsFallback/PendingReportView";
import { UnavailableReportView } from "views/applications-id-report/components/ReportsFallback/UnavailableReportView";

export const CreditReportByStatus = ({
  verificationCreditReport,
  isFetched,
  applicant,
  applicationMeta,
  screeningCriteria,
  selectedVerificationId,
  activeReportId,
  openRerunModal,
  setOpenRerunModal,
  isIdentityVerificationOverrideRequired
}: {
  verificationCreditReport?: VerificationReport<CreditReport>;
  isFetched?: boolean;
  applicant: Applicant;
  applicationMeta: DefinedQueryObserverResult<Application | undefined, unknown>;
  screeningCriteria?: ApplicantSettings;
  selectedVerificationId: number;
  activeReportId?: number;
  openRerunModal: boolean;
  setOpenRerunModal: (val: boolean) => void;
  isIdentityVerificationOverrideRequired: boolean;
}) => {
  if (
    verificationCreditReport?.status === VerificationStatus.Pending &&
    verificationCreditReport.settings?.execution_mode === ExecutionModes.Manual
  )
    return (
      <ManualReportView
        refetch={applicationMeta.refetch}
        applicantStatus={
          applicationMeta.data!.applicants.find(
            (appl) => appl.id === applicant.id
          )?.application_status
        }
        applicationStatus={applicationMeta.data!.status}
        applicantId={applicant.id}
        verificationId={verificationCreditReport.id}
        applicationId={applicationMeta.data!.id}
        verificationName={VerificationNames.CreditReport}
        creditReportStatus={verificationCreditReport.status}
      />
    );

  if (
    (verificationCreditReport?.status === VerificationStatus.Pending ||
      verificationCreditReport?.status === VerificationStatus.Skipped) &&
    verificationCreditReport.settings?.execution_mode ===
      ExecutionModes.Conditionally
  )
    return (
      <SkippedReportView
        refetch={applicationMeta.refetch}
        applicantId={applicant.id}
        verificationId={verificationCreditReport.id}
        applicationId={applicationMeta.data!.id}
        verificationStatus={verificationCreditReport.status}
      />
    );

  if (
    verificationCreditReport?.status === VerificationStatus.Pending &&
    (verificationCreditReport.is_rerun ||
      applicant.application_status === ApplicantStatus.Submitted)
  )
    return (
      <ProgressReportView
        message={
          verificationCreditReport.is_rerun
            ? "Report re-run in progress. Please wait"
            : undefined
        }
      />
    );

  if (isIdentityVerificationOverrideRequired)
    return (
      <PendingReportView message="Please review issues with applicant's identity verification report. This report will not run until identity is verified" />
    );

  if (verificationCreditReport?.status === VerificationStatus.Pending)
    return <PendingReportView />;

  if (verificationCreditReport?.status === VerificationStatus.Canceled)
    return <PendingReportView message="Report was canceled" />;

  if (verificationCreditReport?.status === VerificationStatus.Started)
    return <ProgressReportView />;

  if (verificationCreditReport?.status === VerificationStatus.NoDataAvailable)
    return <UnavailableReportView />;

  return (
    <CreditReportSlice
      creditReport={verificationCreditReport}
      isFetched={isFetched}
      applicant={applicant}
      screeningCriteria={screeningCriteria}
      applicationStatus={applicationMeta.data!.status}
      verificationId={selectedVerificationId}
      activeReportId={activeReportId}
      openRerunModal={openRerunModal}
      setOpenRerunModal={setOpenRerunModal}
      pastDueAccountType={
        applicationMeta.data!.verification_template?.screening_criteria
          ? applicationMeta.data!.verification_template.screening_criteria[
              `${applicant.role}_settings`
            ]?.past_due_account?.past_due_account_rate
          : ""
      }
    />
  );
};
